import { APP_API_V1_BASE } from "../../../../config/api";
/**
 * DevicePickerModal
 *
 * Lets the user search their org's devices and bind one to an asset.
 * Opened from AssetDetail.jsx → binding panel → "Bind to device" button.
 *
 * Props:
 *   open        {boolean}
 *   onClose     {() => void}
 *   onSuccess   {(binding, updatedAsset) => void}
 *   asset       {object}  — the asset being bound (id, serial_number, asset_type)
 *   token       {string}
 *   activeOrg   {object|null}
 *
 * APIs:
 *   GET  /api/v1/devices?q=&page=&per_page=   — search org devices
 *   POST /api/v1/inventory/bindings            — create binding
 *
 * Flow:
 *   Step 0 — Search & select device
 *   Step 1 — Confirm binding
 *   Step 2 — Done (success screen)
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DevicesIcon from "@mui/icons-material/Devices";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import axios from "axios";

const API_BASE = `${APP_API_V1_BASE}`;
const INVENTORY_API = `${API_BASE}/inventory`;
const DEVICES_API = `${API_BASE}/devices`; // adjust if your devices endpoint differs

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function DevicePickerModal({
  open,
  onClose,
  onSuccess,
  asset,
  token,
  activeOrg,
}) {
  const [step, setStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // ── Reset on open ──
  useEffect(() => {
    if (open) {
      setStep(0);
      setSearchQuery("");
      setDevices([]);
      setDevicesError(null);
      setSelectedDevice(null);
      setSubmitting(false);
      setSubmitError("");
      setResult(null);
    }
  }, [open]);

  // ── Fetch devices ──
  const fetchDevices = useCallback(
    async (q = "") => {
      setDevicesLoading(true);
      setDevicesError(null);
      try {
        const res = await axios.get(DEVICES_API, {
          headers: authHeaders,
          params: {
            per_page: 20,
            page: 1,
            // Only show devices that are not already bound to an asset
            // If your devices API supports this filter — add it here.
            // is_bound: false,
            ...(q.trim() && { q: q.trim() }),
          },
        });
        // Support both { data: [...] } and flat array response shapes
        setDevices(res.data.data ?? res.data ?? []);
      } catch (err) {
        setDevicesError(
          err.response?.status === 404
            ? "No devices found in your organisation."
            : "Failed to load devices — check your connection.",
        );
        setDevices([]);
      } finally {
        setDevicesLoading(false);
      }
    },
    [token, activeOrg],
  );

  // Load devices when modal opens
  useEffect(() => {
    if (open && step === 0) fetchDevices(searchQuery);
  }, [open]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchDevices(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Bind ──
  const handleBind = async () => {
    if (!selectedDevice) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await axios.post(
        `${INVENTORY_API}/bindings`,
        {
          inventory_asset_id: asset.id,
          device_id: selectedDevice.id,
        },
        { headers: authHeaders },
      );

      setResult(res.data);
      setStep(2);
      onSuccess(res.data.binding, res.data.asset);
    } catch (err) {
      setSubmitError(
        err.response?.data?.error || "Binding failed — please try again",
      );
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  return (
    <Dialog
      open={open}
      onClose={step === 2 ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 0 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Bind to device
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.3}>
              {asset.serial_number} · {asset.asset_type}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {step < 2 && (
          <Stepper activeStep={step} sx={{ mt: 2, mb: 0.5 }} alternativeLabel>
            {["Select device", "Confirm"].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogTitle>

      {/* ── Step 0: Select device ── */}
      {step === 0 && (
        <>
          <DialogContent dividers sx={{ p: 0 }}>
            {/* Search bar */}
            <Box
              px={2.5}
              pt={2}
              pb={1.5}
              sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search by device name or ID…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: devicesLoading && (
                    <InputAdornment position="end">
                      <CircularProgress size={14} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Device list */}
            <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
              {devicesError && (
                <Box p={2.5}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    {devicesError}
                  </Alert>
                </Box>
              )}

              {!devicesLoading && !devicesError && devices.length === 0 && (
                <Box textAlign="center" py={5}>
                  <DevicesIcon
                    sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    No devices found
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {searchQuery
                      ? "Try a different search term"
                      : "No unbound devices in your organisation"}
                  </Typography>
                </Box>
              )}

              {devices.length > 0 && (
                <List disablePadding>
                  {devices.map((device, idx) => {
                    const isSelected = selectedDevice?.id === device.id;
                    return (
                      <React.Fragment key={device.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => setSelectedDevice(device)}
                            sx={{
                              px: 2.5,
                              py: 1.5,
                              "&.Mui-selected": {
                                bgcolor: "primary.50",
                                "&:hover": { bgcolor: "primary.50" },
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {isSelected ? (
                                <CheckCircleIcon
                                  color="primary"
                                  fontSize="small"
                                />
                              ) : (
                                <RadioButtonUncheckedIcon
                                  sx={{ color: "text.disabled" }}
                                  fontSize="small"
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={600}>
                                  {device.name ||
                                    device.device_identifier ||
                                    `Device ${device.id?.slice(0, 8)}`}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontFamily: "monospace", fontSize: 11 }}
                                >
                                  {device.device_identifier || device.id}
                                </Typography>
                              }
                            />
                            {/* Device status badge */}
                            {device.status && (
                              <Chip
                                label={device.status}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 10, height: 20, ml: 1 }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                        {idx < devices.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>

            {/* Selected summary bar */}
            {selectedDevice && (
              <Box
                px={2.5}
                py={1.5}
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "primary.50",
                }}
              >
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={700}
                >
                  Selected:{" "}
                  {selectedDevice.name ||
                    selectedDevice.device_identifier ||
                    selectedDevice.id?.slice(0, 16)}
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!selectedDevice}
              onClick={() => setStep(1)}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 1: Confirm ── */}
      {step === 1 && (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Confirm the binding details below. The asset's status will change
              to
              <strong> DEPLOYED</strong> once bound.
            </Typography>

            {/* Summary */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {[
                {
                  label: "Asset serial",
                  value: asset.serial_number,
                  mono: true,
                },
                { label: "Asset type", value: asset.asset_type },
                {
                  label: "Device",
                  value:
                    selectedDevice?.name || selectedDevice?.device_identifier,
                  mono: false,
                },
                { label: "Device ID", value: selectedDevice?.id, mono: true },
                { label: "Status after", value: "DEPLOYED" },
              ].map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  px={2}
                  py={1.2}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                    bgcolor:
                      i % 2 === 0 ? "background.default" : "background.paper",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 140 }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: row.mono ? "monospace" : "inherit",
                      fontSize: row.mono ? 12 : "inherit",
                    }}
                  >
                    {row.value || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setStep(0);
                setSubmitError("");
                setSubmitting(false);
              }}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleBind}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <LinkIcon fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 140,
              }}
            >
              {submitting ? "Binding…" : "Confirm bind"}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 2: Done ── */}
      {step === 2 && result && (
        <>
          <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
            <Box textAlign="center" py={2}>
              <CheckCircleIcon
                sx={{ fontSize: 52, color: "success.main", mb: 2 }}
              />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Asset bound successfully
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                <strong>{asset.serial_number}</strong> is now deployed on{" "}
                <strong>
                  {selectedDevice?.name ||
                    selectedDevice?.device_identifier ||
                    "the selected device"}
                </strong>
                .
              </Typography>

              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "left",
                }}
              >
                {[
                  {
                    label: "Binding ID",
                    value: result.binding?.id,
                    mono: true,
                  },
                  {
                    label: "Bound at",
                    value: result.binding?.bound_at
                      ? new Date(result.binding.bound_at).toLocaleString(
                          "en-IN",
                        )
                      : "—",
                    mono: false,
                  },
                  {
                    label: "New status",
                    value: result.asset?.status,
                    mono: false,
                  },
                ].map((row, i) => (
                  <Box
                    key={i}
                    display="flex"
                    justifyContent="space-between"
                    py={0.8}
                    sx={{
                      borderBottom: i < 2 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {row.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        fontFamily: row.mono ? "monospace" : "inherit",
                        fontSize: row.mono ? 11 : 12,
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
